/**
 * 产品列表页面 — ProductList。
 *
 * GET /api/products 获取列表（支持公司/品牌筛选 + 搜索）。
 * 顶部：品牌筛选器 + 搜索框（公司从全局 companyStore 读取）。
 * DataTable 展示：产品名 / 所属品牌 / GHS 徽章 / AI 可见性 / 诊断次数 / 最近活动。
 * GHS 徽章分档：≥80 成功色、60–79 信息色、<60 警告色、未诊断灰色。
 * 点击行跳转 /products/{id}。
 *
 * TODO(概念稿待后端补字段): 事实包版本、收录率 —— 现有 GET /api/products
 * 未返回，待接口补充后按概念稿 products.html 信息架构补齐列。
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import { getProducts } from "../api/products";
import { getBrands } from "../api/brands";
import { useCompanyStore } from "../store/companyStore";
import { formatDate } from "../utils/format";
import type { ProductListItem, Brand, PaginatedData } from "../types";

export default function ProductList() {
  const navigate = useNavigate();

  // 从全局 companyStore 读取选中公司
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);

  const [data, setData] = useState<PaginatedData<ProductListItem>>({
    items: [],
    total: 0,
    page: 1,
    size: 20,
  });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 筛选状态（公司从全局 store 读取，品牌作为页面内补充筛选）
  const [selectedBrand, setSelectedBrand] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  /** 当全局公司变化时重新加载品牌列表。 */
  useEffect(() => {
    setSelectedBrand("");
    if (selectedCompanyId) {
      getBrands(selectedCompanyId).then(setBrands).catch(() => setBrands([]));
    } else {
      getBrands().then(setBrands).catch(() => setBrands([]));
    }
  }, [selectedCompanyId]);

  /** 加载产品列表。 */
  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getProducts({
        company_id: selectedCompanyId || undefined,
        brand_id: selectedBrand || undefined,
        page: page + 1,
        size: rowsPerPage,
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取产品列表失败");
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedBrand, page, rowsPerPage]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  /** 表格列定义。 */
  const columns: DataTableColumn<ProductListItem>[] = [
    {
      key: "name",
      label: "产品名称",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.main" }}>
            {row.name}
          </Typography>
          {row.category && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {row.category}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "brand",
      label: "所属品牌",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13 }}>{row.brand_name || "—"}</Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {row.company_name || ""}
          </Typography>
        </Box>
      ),
    },
    {
      key: "ghs",
      label: "GHS 评分",
      align: "center",
      render: (row) => {
        // 未诊断（无诊断记录）显示灰色"待诊断"
        if (row.diagnosis_count === 0) {
          return <Badge label="待诊断" variant="default" />;
        }
        const score = Math.round(row.ghs_score);
        const variant =
          score >= 80 ? "success" : score >= 60 ? "info" : "warning";
        return <Badge label={`${score}`} variant={variant} />;
      },
    },
    {
      key: "ai_visibility",
      label: "AI 可见性",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>
          {row.ai_visibility_rate != null
            ? `${Math.round(row.ai_visibility_rate)}%`
            : "—"}
        </Typography>
      ),
    },
    {
      key: "diagnosis",
      label: "诊断次数",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>{row.diagnosis_count}</Typography>
      ),
    },
    {
      key: "last_activity",
      label: "最近活动",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDate(row.last_activity)}
        </Typography>
      ),
    },
  ];

  /** 过滤搜索。 */
  const filteredItems = data.items.filter((item) => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (
      item.name.toLowerCase().includes(text) ||
      (item.brand_name || "").toLowerCase().includes(text) ||
      (item.company_name || "").toLowerCase().includes(text)
    );
  });

  return (
    <Box>
      <PageHeader
        title="产品"
        breadcrumbs={[{ label: "资产管理" }, { label: "产品" }]}
      />

      {/* 筛选栏 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                size="small"
                label="品牌"
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  setPage(0);
                }}
                disabled={!selectedCompanyId}
              >
                <MenuItem value="">全部品牌</MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="搜索产品名称、品牌..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchOutlined sx={{ color: "text.secondary", fontSize: 18, mr: 1 }} />
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 数据表格 */}
      {loading ? (
        <Loading fullPage text="正在加载产品列表..." />
      ) : error ? (
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={filteredItems}
          total={data.total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setRowsPerPage(size);
            setPage(0);
          }}
          onRowClick={(row) => navigate(`/products/${row.id}`)}
          getRowId={(row) => row.id}
          emptyText="暂无产品数据"
        />
      )}
    </Box>
  );
}
